// Google Analytics 4 con Consent Mode v2 — continuità di monitoraggio col legacy.
//
// Attivo SOLO se VITE_GA_ID (Measurement ID, G-XXXXXXXXXX) è valorizzato nella build:
// in dev e staging la variabile manca → tutto è no-op (pattern fallback-innocuo del
// progetto, niente traffico finto in Analytics).
//
// Consenso (GDPR/Garante): gtag parte con tutto DENIED; il banner
// (components/ConsentBanner.tsx) aggiorna il consenso alla scelta dell'utente, che
// viene ricordata in localStorage. Prima del consenso GA invia solo ping anonimi
// senza cookie (comportamento Consent Mode), dopo il rifiuto resta così.
//
// Tre scelte (ADR 0030): 'denied' = niente; 'granted' = solo statistiche (il valore storico,
// chi l'ha già dato non viene ridisturbato); 'all' = statistiche + misurazione delle
// conversioni pubblicitarie (ad_storage e ad_user_data). La personalizzazione degli annunci
// (ad_personalization) resta sempre negata: non facciamo remarketing.
//
// NB quando verrà enforced la CSP: servono script-src https://www.googletagmanager.com
// e connect-src https://*.google-analytics.com https://*.analytics.google.com.

const GA_ID: string = import.meta.env.VITE_GA_ID ?? ''
const STORAGE_KEY = 'spdh-consent-analytics'

export type ConsentChoice = 'all' | 'granted' | 'denied'

/** Evento emesso su window quando il visitatore sceglie: lo ascolta lib/attribution.ts. */
export const CONSENT_EVENT = 'spdh-consent'

export const analyticsEnabled = GA_ID !== ''

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

export function getStoredConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'all' || v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

// Bootstrap di gtag.js: consenso default negato PRIMA del config, poi ripristino
// dell'eventuale scelta già espressa. Idempotente (no-op se già inizializzato).
export function initAnalytics(): void {
  if (!analyticsEnabled || typeof window.gtag === 'function') return

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // gtag.js si aspetta l'oggetto `arguments`, non un array spread.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })
  const stored = getStoredConsent()
  if (stored) window.gtag('consent', 'update', consentState(stored))

  window.gtag('js', new Date())
  window.gtag('config', GA_ID)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`
  document.head.appendChild(script)
}

function consentState(choice: ConsentChoice) {
  const statistiche = choice === 'denied' ? 'denied' : 'granted'
  const pubblicita = choice === 'all' ? 'granted' : 'denied'
  return {
    analytics_storage: statistiche,
    ad_storage: pubblicita,
    ad_user_data: pubblicita,
    ad_personalization: 'denied',
  }
}

// Scelta corrente in memoria: vale anche se localStorage è bloccato (la scelta dura la sessione).
let sceltaInSessione: ConsentChoice | null = null

export function setConsent(choice: ConsentChoice): void {
  if (!analyticsEnabled) return
  sceltaInSessione = choice
  try {
    localStorage.setItem(STORAGE_KEY, choice)
  } catch {
    // storage pieno/bloccato: il consenso vale comunque per la sessione corrente
  }
  window.gtag('consent', 'update', consentState(choice))
  try {
    window.dispatchEvent(new Event(CONSENT_EVENT))
  } catch {
    // ambienti senza Event: chi ascolta rilegge il consenso al prossimo invio
  }
}

/** Il visitatore ha accettato la misurazione delle conversioni pubblicitarie. */
export function hasAdsConsent(): boolean {
  if (!analyticsEnabled) return false
  return (sceltaInSessione ?? getStoredConsent()) === 'all'
}

// Page view per le navigazioni SPA (il primo page_view lo invia il config).
// initAnalytics() qui dentro è la rete di sicurezza contro la race col ConsentBanner:
// gli effect di rotta (es. funnel_step al mount) possono girare PRIMA dell'effect del
// banner — senza coda dataLayer già pronta quegli eventi andavano persi (940 funnel_step
// contro 1.372 pageview, ago 2026). L'init è idempotente e mette il consent default
// PRIMA del config, quindi l'ordine resta corretto anche partendo da qui.
export function trackPageView(path: string): void {
  if (!analyticsEnabled) return
  initAnalytics()
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

// Evento custom (es. page_not_found per il monitoraggio dei 404).
export function trackEvent(name: string, params: Record<string, string> = {}): void {
  if (!analyticsEnabled) return
  initAnalytics()
  window.gtag('event', name, params)
}

// Client ID GA4 del visitatore (asincrono: gtag lo fornisce solo a script caricato).
// Usato per correlare le conversazioni Chatwoot col comportamento sul sito
// (stesso client che poi completa ordine_inviato = la chat ha prodotto un ordine).
export function getGaClientId(cb: (id: string | undefined) => void): void {
  if (!analyticsEnabled) {
    cb(undefined)
    return
  }
  initAnalytics()
  window.gtag('get', GA_ID, 'client_id', (id: unknown) => cb(typeof id === 'string' ? id : undefined))
}
